/* @flow */

import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { compose } from 'recompose';
import { values } from 'lodash/fp';
import { getSportCategories } from 'app/actions/sportCategoriesActions';
import { resetSportCategories } from 'app/actions/lobbyActions';
import Responsive from 'app/components/responsive/Responsive';
import FilterBox from 'app/components/sportsFilters/FilterBox';
import MobileFilterBox from 'app/components/sportsFilters/MobileFilterBox';
import { isInvalidCategory } from '../../utils/sportsFilterUtils';
import { joinUntilFalsy } from '../../utils/stringUtils';

type Props = {
  activeSport?: string,
  activeFilter?: string,
  activeSubCategory?: string,
  categorySlug?: ?string,
  hasInitialised: boolean,
  history: Object,
  isLoading?: boolean,
  namespace: string,
  onLoad: () => void,
  onResetSportCategories: () => Object,
  onSportSelect: (sport: string, subCategory?: ?string) => Object,
  showAll?: boolean,
  showFeatured?: boolean,
  showSubCategories?: boolean,
  sportCategories: Object,
  sportsList: Array<Object>,
  sportSlug?: ?string
};

class SportsFilterBox extends React.Component<Props> {
  props: Props;

  static defaultProps = {
    activeSport: undefined,
    activeFilter: undefined,
    activeSubCategory: undefined,
    categorySlug: undefined,
    isLoading: false,
    showAll: false,
    showFeatured: false,
    showSubCategories: false,
    sportSlug: undefined
  };

  componentDidMount() {
    const { hasInitialised, onLoad } = this.props;

    if (hasInitialised) {
      this.handleInitLoad();
    } else {
      onLoad();
    }
  }

  componentDidUpdate(prevProps) {
    const { hasInitialised } = this.props;
    if (!hasInitialised) return;
    if (!prevProps.hasInitialised && hasInitialised) {
      this.handleInitLoad();
    } else if (hasInitialised) {
      this.setStoreFromRoute();
    }
  }

  // We have to flick between 2 difference sources of truth. (Store or route)
  // 1. When this component first mounts, if there is no sport present in the route,
  // then we load the route from the store.
  // 2. Otherwise the route will update the store.
  // This logic is pretty complicated and should probably be abstracted out of this component.
  //
  handleInitLoad() {
    const { sportSlug } = this.props;
    if (!sportSlug) {
      this.loadRouteFromStore();
    } else {
      this.setStoreFromRoute();
    }
  }

  loadRouteFromStore() {
    const { activeSport, namespace, activeSubCategory, history } = this.props;
    if (activeSport) {
      history.replace({
        ...history.location,
        pathname: joinUntilFalsy([namespace, activeSport, activeSubCategory], '/')
      });
    }
  }

  setStoreFromRoute() {
    const {
      activeSubCategory,
      activeSport,
      sportSlug,
      categorySlug,
      sportCategories,
      onSportSelect
    } = this.props;
    if (sportSlug === activeSport && categorySlug === activeSubCategory) return; // nothing to do
    if (!sportSlug || isInvalidCategory(sportCategories.items, sportSlug, categorySlug)) {
      // reset the route and the store
      this.resetRouteAndStore();
    } else if (sportSlug) {
      onSportSelect(sportSlug, categorySlug);
    }
  }

  resetRouteAndStore() {
    const { history, onResetSportCategories, namespace } = this.props;
    history.replace({
      ...history.location,
      pathname: namespace
    });
    onResetSportCategories();
  }

  render() {
    const {
      activeSport,
      activeFilter,
      activeSubCategory,
      namespace,
      isLoading,
      showAll,
      showFeatured,
      showSubCategories,
      sportsList
    } = this.props;

    return (
      <Responsive desktop>
        {isDesktop =>
          isDesktop ? (
            <FilterBox
              activeSport={activeSport}
              activeFilter={activeFilter}
              activeSubCategory={activeSubCategory}
              isLoading={isLoading}
              namespace={namespace}
              showAll={showAll}
              showFeatured={showFeatured}
              showSubCategories={showSubCategories}
              sportsList={sportsList}
            />
          ) : (
            <MobileFilterBox
              activeSport={activeSport}
              activeFilter={activeFilter}
              activeSubCategory={activeSubCategory}
              isLoading={isLoading}
              namespace={namespace}
              showAll={showAll}
              showFeatured={showFeatured}
              showSubCategories={showSubCategories}
              sportsList={sportsList}
            />
          )
        }
      </Responsive>
    );
  }
}

function mapStateToProps({ sportCategories }: GlobalState, { history, match }) {
  return {
    history,
    hasInitialised: sportCategories.hasInitialised,
    isLoading: sportCategories.isFetching && !sportCategories.hasInitialised,
    sportCategories,
    sportsList: values(sportCategories.items),
    sportSlug: match.params.sport,
    categorySlug: match.params.category
  };
}

const actions = {
  onLoad: getSportCategories,
  onResetSportCategories: resetSportCategories
};

export default compose(withRouter, connect(mapStateToProps, actions))(SportsFilterBox);
